import React, { useRef, useState } from 'react';
import { Calendar, Pencil } from 'lucide-react';
import { formatDateSafe, toISODateString } from '../../utils/dateHelpers';

export interface InlineDatePickerProps {
  value: any;
  onChange: (date: Date) => void;
  fallback?: string;
  icon?: React.ReactNode;
  className?: string;
  /** Render the formatted date with overdue/custom styling */
  renderLabel?: (formatted: string) => React.ReactNode;
  disabled?: boolean;
}

export const InlineDatePicker: React.FC<InlineDatePickerProps> = ({
  value,
  onChange,
  fallback = 'No date',
  icon,
  className = '',
  renderLabel,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    inputRef.current?.showPicker?.();
    inputRef.current?.focus();
    setIsOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) return;
    const [y, m, d] = e.target.value.split('-').map(Number);
    const newDate = new Date(y, m - 1, d);
    const currentDate = value ? new Date(value) : null;
    if (!currentDate || newDate.toDateString() !== currentDate.toDateString()) {
      onChange(newDate);
    }
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const val = inputRef.current?.value;
      if (val) {
        const [y, m, d] = val.split('-').map(Number);
        const newDate = new Date(y, m - 1, d);
        const currentDate = value ? new Date(value) : null;
        if (!currentDate || newDate.toDateString() !== currentDate.toDateString()) {
          onChange(newDate);
        }
      }
      setIsOpen(false);
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const formatted = formatDateSafe(value, fallback);
  const isoValue = value ? toISODateString(value) : '';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 cursor-pointer group transition-colors hover:text-accent ${disabled ? 'opacity-50 cursor-default' : ''} ${className}`}
    >
      {icon !== undefined ? icon : <Calendar className="w-4 h-4 text-txt-muted group-hover:text-accent transition-colors" strokeWidth={1.5} />}
      {renderLabel ? renderLabel(formatted) : (
        <span className="group-hover:text-accent transition-colors">{formatted}</span>
      )}
      {!disabled && (
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" strokeWidth={1.5} />
      )}
      <input
        ref={inputRef}
        type="date"
        value={isoValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setIsOpen(false)}
        onClick={(e) => e.stopPropagation()}
        className="absolute w-0 h-0 opacity-0 pointer-events-none"
        tabIndex={-1}
      />
    </button>
  );
};
